import { type Locale, type PhotoDescriptor, adminApi } from '@/lib/api';
import { photoUrl } from '@levamar/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

const S3_PUBLIC =
  (import.meta.env.VITE_S3_PUBLIC_URL as string | undefined) ?? 'http://localhost:9000/levamar-dev';
const LOCALES: Locale[] = ['pt', 'en', 'fr', 'es', 'de'];

interface Props {
  vehicleId: string;
  photos: PhotoDescriptor[];
  onChange: () => void;
}

/**
 * Photos panel: multi-upload, per-photo 5-locale alt editor, reorder via position field,
 * delete. The S3 keys are the logical prefix only — full URLs are composed here with the
 * largest WebP rendition (1280) which next/image still resamples down for the public site.
 */
export function PhotosPanel({ vehicleId, photos, onChange }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    setUploadError(null);
    const files = Array.from(e.target.files ?? []);
    if (e.target) e.target.value = '';
    if (files.length === 0) return;
    setUploading(files.length);
    for (const f of files) {
      try {
        await adminApi.uploadPhoto(vehicleId, f);
      } catch (err) {
        setUploadError(`${f.name}: ${(err as Error).message}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    onChange();
  }

  return (
    <section style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="label" style={{ color: 'var(--lv-brand)' }}>
          FOTOS · GALERIA
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={btnGhost}
          disabled={uploading > 0}
        >
          {uploading > 0 ? `A enviar ${uploading}…` : '+ Adicionar foto'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={onPick}
          style={{ display: 'none' }}
        />
      </div>

      {uploadError && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            background: '#fce4e4',
            color: 'var(--lv-err)',
            fontSize: 12,
          }}
        >
          {uploadError}
        </div>
      )}

      {photos.length === 0 ? (
        <div
          style={{
            marginTop: 16,
            padding: 32,
            textAlign: 'center',
            color: 'var(--lv-gray-500)',
            fontSize: 13,
            border: '1px dashed var(--lv-line)',
          }}
        >
          Ainda sem fotos. A página pública mostra a silhueta SVG como fallback.
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '16px 0 0',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          {photos.map((p) => (
            <PhotoCard
              key={p.key}
              vehicleId={vehicleId}
              photo={p}
              onChange={onChange}
              totalCount={photos.length}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PhotoCard({
  vehicleId,
  photo,
  onChange,
  totalCount,
}: {
  vehicleId: string;
  photo: PhotoDescriptor;
  onChange: () => void;
  totalCount: number;
}) {
  const [editing, setEditing] = useState(false);
  const [alt, setAlt] = useState(photo.alt);

  const patch = useMutation({
    mutationFn: (body: { alt?: typeof alt; position?: number }) =>
      adminApi.patchPhoto(vehicleId, photo.key, body),
    onSuccess: () => {
      setEditing(false);
      onChange();
    },
  });
  const remove = useMutation({
    mutationFn: () => adminApi.deletePhoto(vehicleId, photo.key),
    onSuccess: onChange,
  });

  // NOT a hardcoded `-640`: the pipeline never upscales, so a source narrower
  // than 640px has no such rendition and the tile rendered broken.
  const url = photoUrl(S3_PUBLIC, photo, 640);
  const completeness = LOCALES.filter((l) => alt[l]?.trim().length > 0).length;

  return (
    <li style={{ border: '1px solid var(--lv-line)', background: '#fafaf7', overflow: 'hidden' }}>
      <div style={{ position: 'relative', aspectRatio: '4 / 3', background: '#0c2322' }}>
        <img
          src={url}
          alt={photo.alt.pt || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          loading="lazy"
        />
        {photo.position === 0 && (
          <span
            style={{
              position: 'absolute',
              top: 6,
              left: 6,
              background: 'var(--lv-brand)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              padding: '3px 6px',
              textTransform: 'uppercase',
            }}
          >
            COVER
          </span>
        )}
      </div>

      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>alt_T: {completeness}/5 locales</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => setEditing(!editing)} style={btnSmall}>
            {editing ? 'Fechar' : 'alt_T'}
          </button>
          <select
            value={photo.position}
            onChange={(e) => patch.mutate({ position: Number(e.target.value) })}
            style={{ ...btnSmall, padding: '4px 6px' }}
          >
            {Array.from({ length: totalCount }, (_, i) => (
              <option key={i} value={i}>
                {i === 0 ? 'Capa' : `Foto ${i + 1}`}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => remove.mutate()} style={btnSmallDanger}>
            ✕
          </button>
        </div>

        {editing && (
          <div style={{ marginTop: 4, display: 'grid', gap: 4 }}>
            {LOCALES.map((l) => (
              <label
                key={l}
                style={{ display: 'grid', gridTemplateColumns: '32px 1fr', gap: 4, alignItems: 'center' }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--lv-gray-500)',
                    textTransform: 'uppercase',
                  }}
                >
                  {l}
                </span>
                <input
                  type="text"
                  value={alt[l]}
                  onChange={(e) => setAlt({ ...alt, [l]: e.target.value })}
                  style={{ ...btnSmall, padding: '4px 6px', textAlign: 'left' }}
                  placeholder={`Descreva a foto em ${l.toUpperCase()}`}
                />
              </label>
            ))}
            <button
              type="button"
              onClick={() => patch.mutate({ alt })}
              disabled={patch.isPending}
              style={{
                ...btnSmall,
                background: 'var(--lv-brand)',
                color: '#fff',
                borderColor: 'var(--lv-brand)',
              }}
            >
              {patch.isPending ? '…' : 'Guardar alt_T'}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnSmall: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '4px 8px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  textAlign: 'center',
};
const btnSmallDanger: React.CSSProperties = {
  ...btnSmall,
  borderColor: 'var(--lv-err)',
  color: 'var(--lv-err)',
};
